/*
 * GoFatbike custom Web Components.
 * Loaded as a module after theme.js from layout/theme.liquid. theme.js stays untouched.
 */

/* ------------------------------------------------------------------------------------------------
 * <go-search>: search overlay (sections/go-search.liquid)
 * Fragments: go-search-results (predictive), go-search-recent, go-search-products (bestsellers/fallback)
 * ---------------------------------------------------------------------------------------------- */

const SEARCH_DEBOUNCE_MS = 280;
const SEARCH_CACHE_LIMIT = 20;
const RECENT_STORAGE_KEY = 'theme:recently-viewed-products';
const RECENT_LIMIT = 2;
const RAIL_SCROLLBAR_HIDE_MS = 800;
const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

class GoSearch extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.findHooks();
    if (!this.hasRequiredHooks()) return;

    this.abortController = new AbortController();
    this.queryCache = new Map();
    this.idleStarted = false;
    this.fallbackPromise = null;
    this.predictiveController = null;
    this.debounceTimer = null;
    this.frameId = null;
    this.opener = null;
    this.savedOverflow = '';
    this.activeOptionIndex = -1;
    this.railTimers = new WeakMap();

    this.bindEvents();
  }

  disconnectedCallback() {
    if (!this.abortController) return;

    if (this.isOpen()) this.close({ restoreFocus: false });
    this.abortController.abort();
    this.abortController = null;
  }

  /* Setup ---------------------------------------------------------------------------------------- */

  findHooks() {
    this.panel = this.querySelector('[data-go-search-panel-root]');
    this.backdrop = this.querySelector('[data-go-search-backdrop]');
    this.form = this.querySelector('[data-go-search-form]');
    this.input = this.querySelector('[data-go-search-input]');
    this.clearButton = this.querySelector('[data-go-search-clear]');
    this.closeButton = this.querySelector('[data-go-search-close]');
    this.status = this.querySelector('[data-go-search-status]');
    this.error = this.querySelector('[data-go-search-error]');
    this.idleView = this.querySelector('[data-go-search-idle]');
    this.loadingView = this.querySelector('[data-go-search-loading]');
    this.resultsMount = this.querySelector('[data-go-search-results-mount]');
    this.recentGroup = this.querySelector('[data-go-search-recent-group]');
    this.recentMount = this.querySelector('[data-go-search-recent-mount]');
    this.bestsellerGroup = this.querySelector('[data-go-search-bestseller-group]');
    this.bestsellerMount = this.querySelector('[data-go-search-bestseller-mount]');
    this.fallbackHeader = this.querySelector('template[data-go-search-fallback-header]');
  }

  hasRequiredHooks() {
    return Boolean(
      this.id &&
        this.panel &&
        this.backdrop &&
        this.form &&
        this.input &&
        this.closeButton &&
        this.status &&
        this.error &&
        this.idleView &&
        this.loadingView &&
        this.resultsMount
    );
  }

  bindEvents() {
    const options = { signal: this.abortController.signal };

    document.addEventListener('click', (event) => this.onTriggerClick(event), options);
    this.closeButton.addEventListener('click', () => this.close(), options);
    this.backdrop.addEventListener('click', () => this.close(), options);
    document.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.addEventListener('click', (event) => this.onTermClick(event), options);
    this.input.addEventListener('input', () => this.onInput(), options);
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event), options);
    this.form.addEventListener('reset', (event) => this.onReset(event), options);
    window.addEventListener('resize', () => this.scheduleTopUpdate(), options);
    window.addEventListener('scroll', () => this.scheduleTopUpdate(), { ...options, passive: true });
    // Scroll events do not bubble, so rail scrolling is caught in the capture phase
    this.panel.addEventListener('scroll', (event) => this.onRailScroll(event), { ...options, capture: true, passive: true });
  }

  /* Card rails show their scrollbar only while they are being scrolled */
  onRailScroll(event) {
    const rail = event.target;
    if (!(rail instanceof Element) || !rail.matches('.go-search-results__grid, .go-search-recent__list')) return;

    rail.dataset.scrolling = '';
    clearTimeout(this.railTimers.get(rail));
    this.railTimers.set(
      rail,
      setTimeout(() => delete rail.dataset.scrolling, RAIL_SCROLLBAR_HIDE_MS)
    );
  }

  /* Open and close ------------------------------------------------------------------------------- */

  isOpen() {
    return !this.hidden;
  }

  getTriggers() {
    return document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest(`[aria-controls="${CSS.escape(this.id)}"]`);
    if (!trigger || this.contains(trigger)) return;
    if (isModifiedClick(event)) return;

    event.preventDefault();
    this.open(trigger);
  }

  open(trigger) {
    if (this.isOpen()) return;

    // Mobile navigation trigger: close its theme drawer first, and restore focus to the drawer toggle later
    const parentDialog = trigger.closest('[open]');
    const dialogHidden = typeof parentDialog?.hide === 'function' ? parentDialog.hide() : null;

    this.opener = dialogHidden ? parentDialog.controls?.find(isVisible) || trigger : trigger;
    this.resetSearch();
    this.hidden = false;
    this.setTriggersExpanded(true);
    this.updateTop();
    this.savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.loadIdleContent();
    this.input.focus();

    // The drawer's focus trap returns focus to its toggle in a timeout after hiding, so take focus back after that
    dialogHidden?.then(() => {
      setTimeout(() => {
        if (this.isOpen()) this.input.focus();
      });
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen()) return;

    this.resetSearch();
    document.documentElement.style.overflow = this.savedOverflow;
    this.hidden = true;
    this.setTriggersExpanded(false);

    if (restoreFocus && this.opener?.isConnected) this.opener.focus();
    this.opener = null;
  }

  setTriggersExpanded(expanded) {
    this.getTriggers().forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  }

  /* Places the overlay directly below the visible part of the header. On mobile the header's search bar
     (snippets/go-header-search.liquid) is covered, so the overlay's own search box takes its place */
  updateTop() {
    const headerBar = document.querySelector('.go-header-search');
    const header = document.querySelector('.shopify-section--header');
    let top = 0;
    if (headerBar && isVisible(headerBar)) top = headerBar.getBoundingClientRect().top;
    else if (header) top = header.getBoundingClientRect().bottom;
    this.style.setProperty('--go-search-top', `${Math.round(Math.max(0, top))}px`);
  }

  scheduleTopUpdate() {
    if (!this.isOpen() || this.frameId) return;

    this.frameId = requestAnimationFrame(() => {
      this.frameId = null;
      this.updateTop();
    });
  }

  onKeydown(event) {
    if (!this.isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isVisible);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const outside = !this.panel.contains(document.activeElement);

    if (event.shiftKey && (outside || document.activeElement === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (outside || document.activeElement === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  /* View state ----------------------------------------------------------------------------------- */

  resetSearch() {
    this.cancelPredictive();
    this.input.value = '';
    this.updateClearButton();
    this.showIdle();
  }

  showView(view) {
    this.idleView.hidden = view !== 'idle';
    this.loadingView.hidden = view !== 'loading';
    this.resultsMount.hidden = view !== 'results';
    this.clearActiveOption();
    this.updateComboboxState(view);
  }

  updateComboboxState(view) {
    const hasVisibleSuggestions = view === 'results' && this.getVisibleOptions().length > 0;
    this.input.setAttribute('aria-expanded', String(hasVisibleSuggestions));
  }

  showIdle() {
    this.hideError();
    this.setStatus('');
    this.showView('idle');
  }

  showLoading() {
    this.hideError();
    this.setStatus(this.dataset.loadingMessage || '');
    this.showView('loading');
  }

  showResults(fragment) {
    this.hideError();
    this.setStatus('');
    this.resultsMount.replaceChildren(fragment.cloneNode(true));
    this.showView('results');

    if (fragment.dataset.hasProducts !== 'true') this.fillFallback();
  }

  showError() {
    this.error.hidden = false;
    this.setStatus(this.dataset.errorMessage || this.error.textContent.trim());
    this.showView('idle');
  }

  hideError() {
    this.error.hidden = true;
  }

  setStatus(message) {
    this.status.textContent = message;
  }

  updateClearButton() {
    const isEmpty = this.input.value === '';
    if (this.clearButton) this.clearButton.hidden = isEmpty;
  }

  /* Idle content --------------------------------------------------------------------------------- */

  /* Runs once per page: loaded markup stays in the DOM, so reopening shows it without refetching */
  loadIdleContent() {
    if (this.idleStarted) return;
    this.idleStarted = true;

    this.loadRecent();
    this.loadBestsellers();
  }

  async loadRecent() {
    if (!this.recentGroup || !this.recentMount) return;

    const ids = getRecentProductIds(this.dataset.productId);
    if (ids.length === 0) return;

    this.recentGroup.hidden = false;
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = buildUrl('search', { type: 'product', q: query, section_id: 'go-search-recent' });

    const fragment = await fetchFragment(url, '[data-go-search-recent]').catch(() => null);
    if (fragment && Number(fragment.dataset.count) > 0) {
      this.recentMount.replaceChildren(fragment);
    } else {
      this.recentGroup.hidden = true;
    }
  }

  async loadBestsellers() {
    if (!this.bestsellerGroup || !this.bestsellerMount || !this.dataset.bestsellerUrl) return;

    const fragment = await fetchCollectionProducts(this.dataset.bestsellerUrl);
    if (fragment) {
      this.bestsellerMount.replaceChildren(fragment);
    } else {
      this.bestsellerGroup.hidden = true;
    }
  }

  /* Predictive search ---------------------------------------------------------------------------- */

  onInput() {
    this.updateClearButton();
    this.search();
  }

  search() {
    const key = normalizeQuery(this.input.value);
    this.cancelPredictive();

    if (key === '') {
      this.showIdle();
      return;
    }

    const cached = this.queryCache.get(key);
    if (cached) {
      this.showResults(cached);
      return;
    }

    this.showLoading();
    this.debounceTimer = setTimeout(() => this.fetchPredictive(key), SEARCH_DEBOUNCE_MS);
  }

  cancelPredictive() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.predictiveController?.abort();
    this.predictiveController = null;
  }

  async fetchPredictive(key) {
    this.predictiveController = new AbortController();
    const { signal } = this.predictiveController;
    const url = buildUrl('search/suggest', {
      q: this.input.value.trim(),
      section_id: 'go-search-results',
      'resources[type]': 'query,product',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
    });

    try {
      const fragment = await fetchFragment(url, '[data-go-search-results]', signal);
      if (key !== normalizeQuery(this.input.value)) return;

      this.cacheQuery(key, fragment);
      this.showResults(fragment);
    } catch (error) {
      if (error.name === 'AbortError' || key !== normalizeQuery(this.input.value)) return;
      this.showError();
    }
  }

  /* FIFO cache: Map keeps insertion order, so the first key is the oldest */
  cacheQuery(key, fragment) {
    if (this.queryCache.size >= SEARCH_CACHE_LIMIT) {
      this.queryCache.delete(this.queryCache.keys().next().value);
    }
    this.queryCache.set(key, fragment);
  }

  /* No-results fallback -------------------------------------------------------------------------- */

  async fillFallback() {
    const slot = this.resultsMount.querySelector('[data-go-search-fallback]');
    if (!slot) return;

    if (!this.dataset.fallbackUrl || !this.fallbackHeader) {
      slot.remove();
      return;
    }

    this.fallbackPromise ??= fetchCollectionProducts(this.dataset.fallbackUrl);
    const fragment = await this.fallbackPromise;
    if (!slot.isConnected) return;

    if (!fragment) {
      slot.remove();
      return;
    }

    slot.replaceChildren(this.fallbackHeader.content.cloneNode(true), fragment.cloneNode(true));
    slot.removeAttribute('aria-busy');
  }

  /* Suggestions ---------------------------------------------------------------------------------- */

  onTermClick(event) {
    const link = event.target.closest('[data-go-search-term]');
    if (!link || isModifiedClick(event)) return;

    event.preventDefault();
    this.selectTerm(link);
  }

  selectTerm(link) {
    this.input.value = link.dataset.goSearchTerm;
    this.updateClearButton();
    this.input.focus();
    this.search();
  }

  getVisibleOptions() {
    return [...this.querySelectorAll('[data-go-search-term][role="option"]')].filter(isVisible);
  }

  onInputKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      this.selectActiveOption(event);
    }
  }

  moveActiveOption(step) {
    const options = this.getVisibleOptions();
    if (options.length === 0) return;

    // -1 means focus stays on the input itself
    let index = this.activeOptionIndex + step;
    if (index >= options.length) index = -1;
    if (index < -1) index = options.length - 1;

    this.setActiveOption(options, index);
  }

  selectActiveOption(event) {
    const option = this.getVisibleOptions()[this.activeOptionIndex];
    if (!option) return;

    event.preventDefault();
    this.selectTerm(option);
  }

  setActiveOption(options, index) {
    this.clearActiveOption();
    this.activeOptionIndex = index;

    const option = options[index];
    if (!option?.id || !this.resultsMount.contains(option) || !isVisible(option)) return;

    option.setAttribute('aria-selected', 'true');
    option.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', option.id);
  }

  clearActiveOption() {
    this.activeOptionIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.querySelectorAll('[data-go-search-term][role="option"][aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });
  }

  /* Clear ---------------------------------------------------------------------------------------- */

  onReset(event) {
    event.preventDefault();
    this.resetSearch();
    this.input.focus();
  }
}

/* Helpers ---------------------------------------------------------------------------------------- */

function isModifiedClick(event) {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function isVisible(element) {
  return !element.closest('[hidden]') && element.getClientRects().length > 0;
}

function normalizeQuery(value) {
  return value.trim().toLocaleLowerCase();
}

function getRootUrl() {
  return window.Shopify?.routes?.root || '/';
}

/* Builds a same-origin URL under the current locale root, e.g. /nl/search/suggest?q=... */
function buildUrl(path, params = {}) {
  const url = new URL(`${getRootUrl()}${path}`, window.location.origin);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  return url;
}

/* Collection URLs from Liquid may lack the locale prefix; add it once */
function buildCollectionUrl(collectionUrl, params) {
  const root = getRootUrl();
  const path = collectionUrl.startsWith(root) ? collectionUrl.slice(root.length) : collectionUrl.replace(/^\//, '');
  return buildUrl(path, params);
}

async function fetchFragment(url, rootSelector, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);

  const html = await response.text();
  const root = new DOMParser().parseFromString(html, 'text/html').querySelector(rootSelector);
  if (!root) throw new Error(`Missing fragment root: ${rootSelector}`);

  return document.importNode(root, true);
}

/* Resolves with the go-search-products root, or null when it failed or has no products */
async function fetchCollectionProducts(collectionUrl) {
  try {
    const url = buildCollectionUrl(collectionUrl, { section_id: 'go-search-products' });
    const fragment = await fetchFragment(url, '[data-go-search-products]');
    return fragment.dataset.hasProducts === 'true' ? fragment : null;
  } catch {
    return null;
  }
}

function getRecentProductIds(currentProductId) {
  let stored = [];
  try {
    stored = JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(stored)) return [];

  const ids = stored.map(Number).filter((id) => Number.isInteger(id) && id > 0 && String(id) !== currentProductId);
  return [...new Set(ids)].slice(0, RECENT_LIMIT);
}

if (!customElements.get('go-search')) {
  customElements.define('go-search', GoSearch);
}

/**
 * <go-bike-slider>: rotates the bikes in the desktop sidebar of a question blog
 * (sections/go-question-article.liquid).
 * Without JavaScript the first slide shows and the arrows and dots stay hidden.
 * Auto-rotation pauses on hover and keyboard focus and is off for prefers-reduced-motion.
 */
class GoBikeSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) return;

    // The slider only renders from 1024px; when it is visible, fetch every slide image now so a slide
    // never appears with an empty image box. Hidden on phones, so nothing extra loads there.
    if (this.offsetParent !== null) {
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }

    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 5000;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => this.show(dotIndex, true)));
    this.querySelectorAll('[data-control]').forEach((control) => {
      control.hidden = false;
    });

    this.pause = () => this.stop();
    this.resume = () => this.start();
    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  show(nextIndex, manual = false) {
    const count = this.slides.length;
    this.index = (nextIndex + count) % count;
    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.index;
    });
    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false');
    });
    if (manual && !this.matches(':hover') && !this.contains(document.activeElement)) this.start();
  }
}

if (!customElements.get('go-bike-slider')) {
  customElements.define('go-bike-slider', GoBikeSlider);
}
